import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import isGHMC, { formatDateTimeWithoutSec, formatDateToDDMMYYYY, getCurrentDateTime } from "../../../../Common/helper/helpar";
import { MSWManagementMonthlyFilter } from "./MSWManagementMonthlyReportFilter";
import MSWManagementSecondaryCollectionStore from "./store/MSWManagementSecondaryCollectionStore";
import TableSkeleton from "../../../AllotedMaster/TableSkeleton";

export const MSWManagementVehiclesMonthlyReport = () => {
  const { loading, MSWManagementSecondaryCollectionMonthlyReportData } =
    MSWManagementSecondaryCollectionStore();
  const isProd = isGHMC();

  // Lifted filter dates state
  const [filterDates, setFilterDates] = useState({
    fromDate: null,
    toDate: null,
  });

  // Extract dynamic dates for table columns
  const extractDates = (data) => {
    if (!data || data.length === 0) return [];
    const dateKeys = Object.keys(data[0]).filter((key) => key.includes("_Deployed"));
    return dateKeys.map((key) => {
      const originalDate = key.split("_")[0];
      return {
        original: originalDate,
        formatted: formatDateToDDMMYYYY(originalDate),
      };
    });
  };

  const dates = extractDates(MSWManagementSecondaryCollectionMonthlyReportData || []);

  // Totals calculation
  const calculateTotals = (data) => {
    if (!data || data.length === 0) return { allocatedVehicles: 0, dateData: {} };
    const totals = { allocatedVehicles: 0, dateData: {} };
    dates.forEach((date) => {
      totals.dateData[date.original] = { deployed: 0 };
    });

    data.forEach((row) => {
      totals.allocatedVehicles += row.AllottedVehicleCount || 0;
      dates.forEach((date) => {
        totals.dateData[date.original].deployed += parseInt(row[`${date.original}_Deployed`] || "0", 10);
      });
    });
    return totals;
  };

  const totals = calculateTotals(MSWManagementSecondaryCollectionMonthlyReportData);

  const headerStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
    padding: "10px",
    color: "#fff",
    backgroundColor: isProd ? "#0d5da5" : "#EC1117",
  };

  const cellStyles = { textAlign: "center", border: "1px solid #ddd", padding: "10px" };
  const totalRowStyle = { ...cellStyles, backgroundColor: isProd ? "#5fa6eb" : "#f5a9a9", fontWeight: "bold" };
  const noDataStyle = { ...cellStyles, textAlign: "center", padding: "20px", fontSize: "16px" };
  const lightRowStyle = { ...cellStyles, backgroundColor: isProd ? "#aed8ff" : "#F8CCCD" };
  const darkRowStyle = { ...cellStyles, backgroundColor: isProd ? "#8fc9ff" : "#f5a9a9" };

  const hasData = MSWManagementSecondaryCollectionMonthlyReportData && MSWManagementSecondaryCollectionMonthlyReportData.length > 0;
  const storedFilter = localStorage.getItem("MSWMonthlyFilter");
  const parsed = JSON.parse(storedFilter);

  return (
    <div>
      <div className="card mt-3">
        <div className="">
          

          {/* Pass filter setter to the filter component */}
          <MSWManagementMonthlyFilter setFilterDates={setFilterDates} />

          <Paper>
            {loading ? (
              <TableSkeleton />
            ) : (
              <TableContainer component={Paper}>
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerStyles}>S.No</TableCell>
                    <TableCell sx={headerStyles}>Zone</TableCell>
                    <TableCell sx={headerStyles}>Circle</TableCell>
                    <TableCell sx={headerStyles}>Allocated Vehicles</TableCell>

                    {dates.map((date, index) => (
                      <React.Fragment key={`date-${index}`}>
                        <TableCell sx={headerStyles}>{date.formatted}</TableCell>
                        <TableCell sx={headerStyles}>%</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasData ? (
                    <>
                      {MSWManagementSecondaryCollectionMonthlyReportData.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell sx={index % 2 === 0 ? lightRowStyle : darkRowStyle}>{index + 1}</TableCell>
                          <TableCell sx={index % 2 === 0 ? lightRowStyle : darkRowStyle}>{row.ZoneName}</TableCell>
                          <TableCell sx={index % 2 === 0 ? lightRowStyle : darkRowStyle}>{row.CircleName}</TableCell>
                          <TableCell sx={index % 2 === 0 ? lightRowStyle : darkRowStyle}>{row.AllottedVehicleCount}</TableCell>

                          {dates.map((date, dateIndex) => (
                            <React.Fragment key={`row-${index}-date-${dateIndex}`}>
                              <TableCell sx={index % 2 === 0 ? lightRowStyle : darkRowStyle}>{row[`${date.original}_Deployed`] || "0"}</TableCell>
                              <TableCell sx={index % 2 === 0 ? lightRowStyle : darkRowStyle}>{row[`${date.original}_Percentage`] || "0.00"}%</TableCell>
                            </React.Fragment>
                          ))}
                        </TableRow>
                      ))}

                      {/* Total Row */}
                      <TableRow>
                        <TableCell colSpan={3} sx={totalRowStyle}>Total</TableCell>
                        <TableCell sx={totalRowStyle}>{totals.allocatedVehicles}</TableCell>
                        {dates.map((date, dateIndex) => (
                          <React.Fragment key={`total-date-${dateIndex}`}>
                            <TableCell sx={totalRowStyle}>{totals.dateData[date.original]?.deployed || 0}</TableCell>
                            <TableCell sx={totalRowStyle}></TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4 + dates.length * 2} sx={noDataStyle}>
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </div>
      </div>
    </div>
  );
};
